/* @flow */
import * as React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import ManageAccount from 'components/ManageAccount/ManageAccount';
import { getCountForTables } from 'utils/index';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import styles from './AccountCard.module.scss';
import AccountCardInfo from '../AccountCardInfo/AccountCardInfo';
import { formulateUri } from 'tables/components/ReportView/state';
import Tooltip from '@material-ui/core/Tooltip';

export type AccountCardProps = {
  rowData: Object,
};

type InjectedProps = TranslatorProps;

@translate()
@observer
export default class AccountCard extends ComponentWithInjectedProps<
  AccountCardProps,
  {},
  InjectedProps
> {
  @observable showInfo: boolean = false;

  render(): React.Element<*> {
    const { t, rowData } = this.props;
    const link = `/accounts/${rowData.id}`;

    const accName = rowData.name;
    const accNumber = rowData.account_no === 'NA' ? '' : ` - ${rowData.account_no}`;

    return (
      <Card className={styles['account-card']}>
        <ManageAccount disabled={!rowData.is_enabled} title={`${accName}${accNumber}`} to={link} />

        <CardContent className={styles['account-card-content']}>
          <AccountCardInfo
            link={`assets/account/${rowData.id}/asset-type/Rented Open`}
            name={
              <Tooltip title={t('general:asset_rented_tooltip')}>
                <div className={styles['break-word']}>{t('general:asset_rented_plural')}</div>
              </Tooltip>
            }
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.rental_assets_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-assets" />
              </svg>
            }
          />
          <AccountCardInfo
            link={formulateUri(`assets/`, {
              page: 1,
              filters: [
                {
                  key: 'asset_status',
                  values: ['Sold'],
                },
                {
                  key: 'account',
                  values: [rowData.id],
                },
              ],
            })}
            name={
              <Tooltip title={t('general:asset_sale_tooltip')}>
                <div className={styles['break-word']}>{t('general:asset_sale_plural')}</div>
              </Tooltip>
            }
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.sales_assets_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-assets" />
              </svg>
            }
          />
          <AccountCardInfo
            link={`assets/account/${rowData.id}/asset-type/Owned`}
            name={<div className={styles['break-word']}>{t('general:asset_owned_plural')}</div>}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.owned_assets_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-assets" />
              </svg>
            }
          />
          <AccountCardInfo
            link={formulateUri(`orders/`, {
              page: 1,
              filters: [
                {
                  key: 'er_status',
                  values: ['open'],
                },
                {
                  key: 'account',
                  values: [rowData.id],
                },
              ],
            })}
            name={t('general:contract_open_plural')}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.open_contracts_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-contracts" />
              </svg>
            }
          />
          <AccountCardInfo
            link={formulateUri(`orders/`, {
              page: 1,
              filters: [
                {
                  key: 'er_status',
                  values: ['closed'],
                  overrideImplicit: true,
                },
                {
                  key: 'account',
                  values: [rowData.id],
                },
              ],
            })}
            name={t('general:contract_closed_plural')}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.closed_contracts_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-contracts" />
              </svg>
            }
          />
          <AccountCardInfo
            link={{
              pathname: `/accounts/${rowData.id}`,
              state: { tab: 2 },
            }}
            name={t('general:location_plural')}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.locations_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-locations" />
              </svg>
            }
          />
          <AccountCardInfo
            link={formulateUri(`projects/`, {
              page: 1,
              filters: [
                {
                  key: 'account',
                  values: [rowData.id],
                },
              ],
            })}
            name={t('general:project_plural')}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.projects_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-projects" />
              </svg>
            }
          />
          <AccountCardInfo
            link={`cost-centers/account/${rowData.id}`}
            name={t('general:cost_center_plural')}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.cost_centres_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-cost-centers" />
              </svg>
            }
          />
          <AccountCardInfo
            link={{
              pathname: `/accounts/${rowData.id}`,
              state: { tab: 1 },
            }}
            name={t('general:user_plural')}
            disabled={!rowData.is_enabled}
            value={getCountForTables(rowData.users_count)}
            icon={
              <svg>
                <use xlinkHref="#icon-users" />
              </svg>
            }
          />
        </CardContent>
      </Card>
    );
  }
}
